import type { RegionReport, Typology } from '~~/core/ports/repositories';
import type { RouteLocationRaw } from 'vue-router';

import { useSearchUrl } from '~/composables/propertySearch/context';
import {
	PropertyTypeEnum,
	PropertyTypePlurals,
	propertyTypesTranslations
} from '~/core/entities/CRMEnums';
import { cleanLocationQueryFromRoute } from '~/utils/cleanLocationQueryFromPath';
import { REGION_PROPERTY_TYPE_ORDER } from '~/utils/regionPropertyTypeOrder';
import { grammarInflection } from '~~/utils';
import { computed } from 'vue';

export const roomCategories = [
	{ key: 'bedrooms', singular: 'quarto', plural: 'quartos', title: 'Quartos' },
	{ key: 'suites', singular: 'suíte', plural: 'suítes', title: 'Suítes' },
	{ key: 'parkingSpots', singular: 'vaga', plural: 'vagas', title: 'Vagas' },
	{
		key: 'bathrooms',
		singular: 'banheiro',
		plural: 'banheiros',
		title: 'Banheiros'
	}
];

export interface RoomLink {
	amount: number;
	label: string;
	route: RouteLocationRaw;
}

export interface TypologyGroup {
	type: string;
	links: RoomLink[];
}

export interface CategorySection {
	key: string;
	title: string;
	typologies: TypologyGroup[];
}

/**
 * Composable for region properties linking functionality
 */
export async function useRegionPropertiesLinking(
	region: string,
	city: string,
	state: string,
	propertyType: PropertyTypeEnum
) {
	const { buildSearchRoute } = useSearchUrl();
	const key = computed(() => {
		return `region-properties-linking-${region}-${city}-${state}-${propertyType}`;
	});

	const { data: regionReport } = await useAsyncData<RegionReport[] | null>(
		key.value,
		() => {
			const fetcher = $fetch as any;
			return fetcher('/api/region/report', {
				query: {
					regions: [region],
					city,
					state,
					includeTypologies: true
				}
			}) as Promise<RegionReport[] | null>;
		},
		{
			default: () => null,
			lazy: true,
			...getCacheAsyncDataConfig<RegionReport[] | null>(CACHE_TIME)
		}
	);

	const regionData = computed<RegionReport | undefined>(() => {
		return regionReport.value?.[0];
	});

	const availablePropertyTypes = computed(() => {
		const typologies = regionData.value?.typologies || [];

		let filteredTypologies = typologies;
		if (propertyType) {
			filteredTypologies = typologies.filter(
				(typology: Typology) => typology.propertyType === propertyType
			);
		}

		return filteredTypologies
			.sort((a: Typology, b: Typology) => {
				const orderA = REGION_PROPERTY_TYPE_ORDER.indexOf(
					a.propertyType as PropertyTypeEnum
				);
				const orderB = REGION_PROPERTY_TYPE_ORDER.indexOf(
					b.propertyType as PropertyTypeEnum
				);
				return orderA - orderB;
			})
			.slice(0, 4);
	});

	const formatPropertyType = (typology: string): string => {
		if (!typology) return 'Imóveis';
		return PropertyTypePlurals[typology as PropertyTypeEnum] || 'Imóveis';
	};

	const generateRoomLinks = (
		categoryKey: string,
		categoryName: { singular: string; plural: string }
	): TypologyGroup[] => {
		const result: TypologyGroup[] = [];

		if (!regionData.value) return [];

		for (const propertyType of availablePropertyTypes.value) {
			const links: RoomLink[] = [];

			const roomStats =
				propertyType.roomStats?.[
					categoryKey as keyof typeof propertyType.roomStats
				] || [];

			const sortedRoomStats = [...roomStats]
				.sort((a, b) => a.amount - b.amount)
				.slice(0, 4);

			for (const stat of sortedRoomStats) {
				if (stat.propertiesCount > 0) {
					const label = `${PropertyTypePlurals[propertyType.propertyType as PropertyTypeEnum]} com ${stat.amount} ${grammarInflection(
						stat.amount,
						categoryName.singular,
						categoryName.plural
					)}`;

					const route = cleanLocationQueryFromRoute(
						buildSearchRoute({
							states: [regionData.value?.state],
							cities: [regionData.value?.city],
							regions: [regionData.value?.region],
							propertyTypes: [propertyType.propertyType as PropertyTypeEnum],
							[categoryKey]: stat.amount
						})
					);

					links.push({
						amount: stat.amount,
						label,
						route
					});
				}
			}

			if (links.length > 0) {
				result.push({
					type: formatPropertyType(propertyType.propertyType),
					links
				});
			}
		}

		return result;
	};

	const categorySections = computed<CategorySection[]>(() => {
		return roomCategories
			.map(category => {
				return {
					key: category.key,
					title: category.title,
					typologies: generateRoomLinks(category.key, {
						singular: category.singular,
						plural: category.plural
					})
				};
			})
			.filter(section => section.typologies.some(t => t.links.length > 0));
	});

	const primaryPropertyType = computed(() => {
		if (propertyType) {
			return formatPropertyType(propertyType).toLowerCase();
		}

		const validPropertyTypes = regionData.value?.topPropertyTypes || [];
		if (validPropertyTypes.length === 0) {
			return 'imóveis';
		}

		const firstType = validPropertyTypes[0] || '';
		return formatPropertyType(firstType).toLowerCase();
	});

	return {
		categorySections,
		primaryPropertyType,
		availablePropertyTypes,
		formatPropertyType
	};
}
